import { memo, useMemo } from "react";

import { DiscountRoomConditionType } from "@/lib/gql/__generated__/graphql";

import { FeatureIslandContainer, FeatureIslandMain } from "@/components/features/_layouts";

import {
	AccordionLayout,
	DiscountCodeFormFieldComposition,
	DiscountDescriptionFormFieldComposition,
	DiscountEditHeaderComposition,
	DiscountNameFormFieldComposition,
	FeatureIslandLayout,
	IsUncancelableFormFieldComposition,
	Layout,
	LongTermStayFormFieldComposition,
	PriceTotalLimitAmountFormFieldComposition,
	ResourceLowestPriceFormFieldComposition,
	RoomConditionTypeFormFieldComposition,
	SaleTypeForPlanFormFieldComposition,
	SaleTypeForTermFormFieldComposition,
	StayableDayOfTheWeekFormFieldComposition,
} from "../../../_shared";
import { useInitialized } from "../model";

type Props = {
	hotelID: string;
	discountID: string;
};

export const DiscountLongTermStayEditPage = memo(({ hotelID, discountID }: Props) => {
	const {
		//
		viewModel,
		// mutation
		onUpdateDiscountLongTerm,

		//
		// 公開にする
		onPublish,
		// 非公開にする
		onUnpublish,

		// 基本設定
		// ディスカウント名の変更
		onChangeName,
		// ディスカウント説明の変更
		onChangeDescription,
		// ディスカウントコードの変更
		onChangeDiscountCode,
		// 連泊ディスカウントの追加
		onAddLongTermStayDiscountObject,
		// 連泊ディスカウントの割引方法の変更
		onChangeDiscountType,
		// 連泊ディスカウントの泊数の変更
		onChangeIncludeSmallestNights,
		// 連泊ディスカウントのの固定割引の対象の変更
		onChangeStaticTargetType,
		// 連泊ディスカウントの割引の割合額の変更
		onChangeRatio,
		// 連泊ディスカウントの割引の固定額の変更
		onChangeStatic,
		// 連泊ディスカウントの削除
		onDeleteLongTermStayDiscountObject,

		// 追加設定
		// 最低価格の変更
		onChangeResourceLowestPrice,
		// ディスカウント上限金額を設定しない
		onUnSettingDiscountPriceTotalLimit,
		// ディスカウント上限金額の変更
		onChangeDiscountPriceTotalLimit,
		// ディスカウントをプラン限定にするかの変更
		onChangeSaleTypeForPlan,
		// キャンセル不可のディスカウントにするかの変更
		onChangeIsUncancelable,
		// ディスカウントの利用可能期間を設定するかの変更
		onChangeSaleTypeForTerm,
		// 公開期間を変更
		onChangeSale,
		// 利用可能期間を変更
		onChangeStayable,
		// 利用可能曜日の変更
		onChangeStayableDayOfTheWeek,
		// 同じ部屋のみ連泊ディスカウントを適用するかの変更
		onChangeRoomConditionType,
	} = useInitialized(hotelID, discountID);

	/**
	 * @todo
	 * ViewModel でエラーを保持しないようにしている
	 * 利用可能な曜日のエラーを ViewModel で管理していないため、ViewModel が保持しているエラーと利用可能な曜日のエラーを統合する
	 */
	const hasError = useMemo(() => {
		return viewModel.hasError || viewModel.stayableDayOfTheWeek?.length === 0;
	}, [viewModel.hasError, viewModel.stayableDayOfTheWeek?.length]);

	return (
		<FeatureIslandContainer>
			<DiscountEditHeaderComposition
				title="連泊ディスカウントの設定"
				inSale={viewModel.inSale}
				isDirty={viewModel.isDirty}
				hasError={hasError}
				onPublish={onPublish}
				onUnpublish={onUnpublish}
				onSave={onUpdateDiscountLongTerm}
			/>
			<FeatureIslandMain>
				<Layout className="mx-auto grid max-w-screen-lg gap-y-12">
					<FeatureIslandLayout title="基本設定">
						<Layout className="divide-y divide-zinc-100 [&>div]:p-3">
							{/* ディスカウントの名前 */}
							<DiscountNameFormFieldComposition
								discountName={viewModel.name}
								//
								onChangeName={onChangeName}
							/>

							{/* ディスカウントの説明 */}
							<DiscountDescriptionFormFieldComposition
								discountDescription={viewModel.description}
								//
								onChangeDescription={onChangeDescription}
							/>

							{/* ディスカウントコード */}
							<DiscountCodeFormFieldComposition
								discountCode={viewModel.discountCode}
								//
								onChangeCode={onChangeDiscountCode}
							/>

							{/* 連泊ディスカウント */}
							<LongTermStayFormFieldComposition
								longTermStayDiscountObject={viewModel.longTermStayDiscountObject}
								//
								onAddLongTermStayDiscountObject={onAddLongTermStayDiscountObject}
								onChangeDiscountType={onChangeDiscountType}
								onChangeIncludeSmallestNights={onChangeIncludeSmallestNights}
								onChangeStaticTargetType={onChangeStaticTargetType}
								onChangeRatio={onChangeRatio}
								onChangeStatic={onChangeStatic}
								onDeleteLongTermStayDiscountObject={onDeleteLongTermStayDiscountObject}
							/>

							{/* 提供最低価格の設定 */}
							<ResourceLowestPriceFormFieldComposition
								resourceLowestPriceAmount={viewModel.resourceLowestPrice.amount}
								onChangeResourceLowestPrice={onChangeResourceLowestPrice}
							/>
						</Layout>
					</FeatureIslandLayout>

					<FeatureIslandLayout title="追加設定">
						{/* 同じ部屋のみ連泊ディスカウントを適用する */}
						<AccordionLayout
							title="連泊ディスカウントの適用条件に関する設定"
							id="discount-same-room-only-settings"
						>
							<RoomConditionTypeFormFieldComposition
								roomConditionType={
									viewModel.roomConditionType.value ?? DiscountRoomConditionType.AnyRoom
								}
								onChangeRoomConditionType={onChangeRoomConditionType}
							/>
						</AccordionLayout>

						{/* 合計上限価格の設定 */}
						<AccordionLayout
							title="ディスカウント合計上限金額に関する設定"
							id="discount-price-total-limit-settings"
						>
							<PriceTotalLimitAmountFormFieldComposition
								discountPriceTotalLimitAmount={viewModel.discountPriceTotalLimit?.amount}
								onUnSettingDiscountPriceTotalLimit={onUnSettingDiscountPriceTotalLimit}
								onChangeDiscountPriceTotalLimit={onChangeDiscountPriceTotalLimit}
							/>
						</AccordionLayout>

						{/* プラン限定の設定 */}
						<AccordionLayout
							title="プラン限定のディスカウントに関する設定"
							id="discount-sale-type-for-plan-settings"
						>
							<SaleTypeForPlanFormFieldComposition
								saleTypeForPlan={viewModel.saleTypeForPlan}
								onChangeSaleTypeForPlan={onChangeSaleTypeForPlan}
							/>
						</AccordionLayout>

						{/* キャンセル不可のディスカウントにするかの設定 */}
						<AccordionLayout
							title="キャンセル不可のディスカウントに関する設定"
							id="discount-is-uncancelable"
						>
							<IsUncancelableFormFieldComposition
								isUncancelable={viewModel.isUncancelable}
								onChangeIsUncancelable={onChangeIsUncancelable}
							/>
						</AccordionLayout>

						{/* 利用可能な期間の設定 */}
						<AccordionLayout title="利用可能な期間に関する設定" id="discount-sale-type-for-term-settings">
							<SaleTypeForTermFormFieldComposition
								saleTypeForTerm={viewModel.saleTypeForTerm}
								saleStart={viewModel.saleStart ?? undefined}
								saleEnd={viewModel.saleEnd ?? undefined}
								stayableStart={viewModel.stayableStart ?? undefined}
								stayableEnd={viewModel.stayableEnd ?? undefined}
								onChangeSaleTypeForTerm={onChangeSaleTypeForTerm}
								onChangeSale={onChangeSale}
								onChangeStayable={onChangeStayable}
							/>
						</AccordionLayout>

						{/* 利用可能な曜日の設定 */}
						<AccordionLayout
							title="利用可能な曜日に関する設定"
							id="discount-stayable-day-of-the-week-settings"
						>
							<StayableDayOfTheWeekFormFieldComposition
								stayableDayOfTheWeek={viewModel.stayableDayOfTheWeek ?? []}
								onChangeStayableDayOfTheWeek={onChangeStayableDayOfTheWeek}
							/>
						</AccordionLayout>
					</FeatureIslandLayout>
				</Layout>
			</FeatureIslandMain>
		</FeatureIslandContainer>
	);
});
